import React, { useState } from "react";
import { ref, set } from "firebase/database";
import { v4 as uuidv4 } from "uuid";
import { db } from "../services/firebase";
import { FaCode, FaFileCode, FaUpload, FaEye, FaTrash, FaPlus } from "react-icons/fa";
import "./AdminCodeSnippetUpload.css";

function AdminCodeSnippetUpload({ user }) {
  const [quizId, setQuizId] = useState("");
  const [quizTitle, setQuizTitle] = useState("");
  const [topic, setTopic] = useState("");
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [countdown, setCountdown] = useState("30");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [allowReattempt, setAllowReattempt] = useState(false);
  const [showResultAfterSubmit, setShowResultAfterSubmit] = useState(true);
  const [delayedResultTime, setDelayedResultTime] = useState("0");

  const [questions, setQuestions] = useState([]);
  const [showPanel, setShowPanel] = useState(false);

  const addQuestion = () => {
    setQuestions([
      ...questions,
      {
        title: "",
        description: "",
        codeSnippet: "",
        language: "javascript", // default language
        questionType: "code_analysis", // code_analysis, pseudocode, debugging
        marks: 0,
        options: ["", "", "", ""],
        correctAnswer: "",
        explanation: "",
      },
    ]);
  };

  const updateQuestion = (index, field, value) => {
    const updated = [...questions];
    updated[index][field] = value;
    setQuestions(updated);
  };

  const updateOption = (qIndex, oIndex, value) => {
    const updated = [...questions];
    updated[qIndex].options[oIndex] = value;
    setQuestions(updated);
  };

  const removeQuestion = (index) => {
    const updated = questions.filter((_, i) => i !== index);
    setQuestions(updated);
  };

  const handleUpload = async () => {
    const generatedQuizId = quizId || uuidv4();

    if (!quizTitle || !topic || questions.length === 0) {
      alert("Please fill in all fields and add at least one question.");
      return;
    }

    // Validate all questions have required fields
    const invalidQuestions = questions.filter(q => 
      !q.title || !q.description || !q.codeSnippet || 
      q.options.some(opt => !opt) || !q.correctAnswer || !q.explanation
    );

    if (invalidQuestions.length > 0) {
      alert("Please fill all fields for all questions.");
      return;
    }

    const payload = {
      quizTitle,
      topic,
      createdBy: user.email,
      date,
      countdown,
      startTime: startTime || null,
      endTime: endTime || null,
      allowReattempt,
      showResultAfterSubmit,
      delayedResultTime: showResultAfterSubmit ? "0" : delayedResultTime,
      questions: {},
    };

    questions.forEach((q, i) => {
      payload.questions[`q${i + 1}`] = {
        ...q,
        date: date
      };
    });

    try {
      await set(ref(db, `codeSnippetQuestions/${generatedQuizId}`), payload);
      alert("✅ Code snippet quiz uploaded successfully!");
      setQuizId(generatedQuizId);
      setQuizTitle("");
      setTopic("");
      setQuestions([]);
    } catch (error) {
      console.error(error);
      alert("Upload failed.");
    }
  };

  const handleJSONUpload = (e) => {
    const file = e.target.files[0];
    if (!file || file.type !== "application/json") {
      return alert("Please upload a valid .json file");
    }
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = JSON.parse(event.target.result);
        
        // Check if it's an array of questions or a single question
        let questionsArray = [];
        
        if (Array.isArray(data)) {
          // If data is an array of questions
          questionsArray = data;
        } else if (data.questions && Array.isArray(data.questions)) {
          // If data has a questions array
          questionsArray = data.questions;
        } else if (data.title && data.description && data.codeSnippet) {
          // If data is a single question
          questionsArray = [data];
        } else {
          alert("Invalid JSON format. Please provide an array of questions or a single question.");
          return;
        }

        // Validate and set questions
        const validQuestions = questionsArray.filter(q => 
          q.title && q.description && q.codeSnippet && 
          q.options && Array.isArray(q.options) && q.options.length === 4 &&
          q.correctAnswer && q.explanation && q.language && q.questionType
        );

        if (validQuestions.length === 0) {
          alert("No valid questions found in JSON. Each question must have: title, description, codeSnippet, language, questionType, marks, options (array of 4), correctAnswer, explanation");
          return;
        }

        setQuestions(validQuestions);
        alert(`✅ Loaded ${validQuestions.length} questions from JSON.`);
      } catch (error) {
        console.error("JSON parsing error:", error);
        alert("Invalid JSON file");
      }
    };
    reader.readAsText(file);
  };

  const handleTextareaJSON = (e) => {
    try {
      const data = JSON.parse(e.target.value);
      
      // Check if it's an array of questions or a single question
      let questionsArray = [];
      
      if (Array.isArray(data)) {
        // If data is an array of questions
        questionsArray = data;
      } else if (data.questions && Array.isArray(data.questions)) {
        // If data has a questions array
        questionsArray = data.questions;
      } else if (data.title && data.description && data.codeSnippet) {
        // If data is a single question
        questionsArray = [data];
      } else {
        alert("Invalid JSON format. Please provide an array of questions or a single question.");
        return;
      }

      // Validate and set questions
      const validQuestions = questionsArray.filter(q => 
        q.title && q.description && q.codeSnippet && 
        q.options && Array.isArray(q.options) && q.options.length === 4 &&
        q.correctAnswer && q.explanation && q.language && q.questionType
      );

      if (validQuestions.length === 0) {
        alert("No valid questions found in JSON. Each question must have: title, description, codeSnippet, language, questionType, marks, options (array of 4), correctAnswer, explanation");
        return;
      }

      setQuestions(validQuestions);
      alert(`✅ Pasted ${validQuestions.length} questions successfully.`);
    } catch (error) {
      console.error("JSON parsing error:", error);
      alert("Invalid JSON in textarea.");
    }
  };

  const languageOptions = [
    { value: "javascript", label: "JavaScript" },
    { value: "python", label: "Python" },
    { value: "java", label: "Java" },
    { value: "cpp", label: "C++" },
    { value: "c", label: "C" },
    { value: "php", label: "PHP" },
    { value: "ruby", label: "Ruby" },
    { value: "go", label: "Go" },
    { value: "rust", label: "Rust" },
    { value: "swift", label: "Swift" },
    { value: "kotlin", label: "Kotlin" },
    { value: "typescript", label: "TypeScript" },
    { value: "pseudocode", label: "Pseudocode" },
  ];

  const questionTypeOptions = [
    { value: "code_analysis", label: "Code Analysis" },
    { value: "pseudocode", label: "Pseudocode" },
    { value: "debugging", label: "Debugging" },
    { value: "output_prediction", label: "Output Prediction" },
    { value: "algorithm_understanding", label: "Algorithm Understanding" },
  ];

  return (
    <div className="code-snippet-upload-wrapper">
      <h2><FaCode /> Admin Code Snippet Upload Panel</h2>
      <button className="submit-btn" onClick={() => setShowPanel(!showPanel)}>
        {showPanel ? "🙈 Hide Code Snippet Upload" : "📂 Show Code Snippet Upload"}
      </button>

      {showPanel && (
        <div className="code-snippet-upload">
          <div className="form-section">
            <h3>📋 Quiz Details</h3>
            <input
              type="text"
              placeholder="Quiz ID (auto-generated if empty)"
              value={quizId}
              onChange={(e) => setQuizId(e.target.value)}
            />
            <input
              type="text"
              placeholder="Quiz Title"
              value={quizTitle}
              onChange={(e) => setQuizTitle(e.target.value)}
            />
            <input
              type="text"
              placeholder="Topic (e.g., JavaScript Functions, Python Loops)"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
            />
            <input 
              type="date" 
              value={date} 
              onChange={(e) => setDate(e.target.value)} 
            />
            <input
              type="number"
              placeholder="Countdown (minutes)"
              value={countdown}
              onChange={(e) => setCountdown(e.target.value)}
            />
            
            <div className="time-settings">
              <label>Start Time (optional)</label>
              <input
                type="datetime-local"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
              />
              <label>End Time (optional)</label>
              <input
                type="datetime-local"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
              />
            </div>

            <div className="settings">
              <label>
                <input
                  type="checkbox"
                  checked={allowReattempt}
                  onChange={(e) => setAllowReattempt(e.target.checked)}
                />
                Allow Reattempt
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={showResultAfterSubmit}
                  onChange={(e) => setShowResultAfterSubmit(e.target.checked)}
                />
                Show Result Immediately After Submit
              </label>
              {!showResultAfterSubmit && (
                <input
                  type="number"
                  placeholder="Delayed Result Time (minutes)"
                  value={delayedResultTime}
                  onChange={(e) => setDelayedResultTime(e.target.value)}
                />
              )}
            </div>
          </div>

          <hr />
          
          <div className="json-upload-section">
            <h3>📁 JSON Upload</h3>
            <p>Upload JSON with just question data. Each question needs: title, description, codeSnippet, language, questionType, marks, options (array of 4), correctAnswer, explanation</p>
            
            <div className="json-upload-methods">
              <div className="file-upload">
                <h4>📂 Upload JSON File</h4>
                <input 
                  type="file" 
                  accept=".json" 
                  onChange={handleJSONUpload}
                  style={{ marginBottom: "10px" }}
                />
              </div>
              
              <div className="textarea-upload">
                <h4>📋 Paste JSON</h4>
                <textarea
                  rows={8}
                  placeholder='Paste your JSON here...'
                  onBlur={handleTextareaJSON}
                  style={{ 
                    width: "100%", 
                    padding: "10px", 
                    marginBottom: "10px",
                    fontFamily: "monospace",
                    fontSize: "12px"
                  }}
                />
              </div>
            </div>
          </div>

          <hr />
          <div className="questions-section">
            <h3><FaFileCode /> Code Snippet Questions</h3>
            <button onClick={addQuestion} className="add-question-btn">
              <FaPlus /> Add Question
            </button>

            {questions.map((q, qIndex) => (
              <div key={qIndex} className="question-block">
                <div className="question-header">
                  <h4>Question {qIndex + 1}</h4>
                  <button 
                    onClick={() => removeQuestion(qIndex)}
                    className="remove-btn"
                  >
                    <FaTrash />
                  </button>
                </div>

                <input
                  type="text"
                  placeholder="Question Title"
                  value={q.title}
                  onChange={(e) => updateQuestion(qIndex, "title", e.target.value)}
                />

                <textarea
                  placeholder="Question Description"
                  rows={3}
                  value={q.description}
                  onChange={(e) => updateQuestion(qIndex, "description", e.target.value)}
                />

                <div className="code-section">
                  <label>Code Snippet:</label>
                  <textarea
                    placeholder="Paste your code snippet here..."
                    rows={8}
                    value={q.codeSnippet}
                    onChange={(e) => updateQuestion(qIndex, "codeSnippet", e.target.value)}
                    className="code-textarea"
                  />
                </div>

                <div className="question-meta">
                  <select
                    value={q.language}
                    onChange={(e) => updateQuestion(qIndex, "language", e.target.value)}
                  >
                    {languageOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>

                  <select
                    value={q.questionType}
                    onChange={(e) => updateQuestion(qIndex, "questionType", e.target.value)}
                  >
                    {questionTypeOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>

                  <input
                    type="number"
                    placeholder="Marks"
                    value={q.marks}
                    onChange={(e) => updateQuestion(qIndex, "marks", parseInt(e.target.value) || 0)}
                  />
                </div>

                <div className="options-section">
                  <h5>Multiple Choice Options:</h5>
                  {q.options.map((option, oIndex) => (
                    <input
                      key={oIndex}
                      type="text"
                      placeholder={`Option ${oIndex + 1}`}
                      value={option}
                      onChange={(e) => updateOption(qIndex, oIndex, e.target.value)}
                    />
                  ))}
                </div>

                <input
                  type="text"
                  placeholder="Correct Answer (must match one of the options exactly)"
                  value={q.correctAnswer}
                  onChange={(e) => updateQuestion(qIndex, "correctAnswer", e.target.value)}
                />

                <textarea
                  placeholder="Explanation (why this answer is correct)"
                  rows={3}
                  value={q.explanation}
                  onChange={(e) => updateQuestion(qIndex, "explanation", e.target.value)}
                />
              </div>
            ))}
          </div>

          <hr />
          <button onClick={handleUpload} className="upload-btn" disabled={questions.length === 0}>
            <FaUpload /> Upload Code Snippet Quiz ({questions.length} questions)
          </button>

          {questions.length > 0 && (
            <div className="preview-section">
              <h3><FaEye /> Preview</h3>
              <div className="preview-list">
                {questions.map((q, i) => (
                  <div key={i} className="preview-item">
                    <strong>Q{i + 1}: {q.title || "Untitled"}</strong>
                    <p><strong>Type:</strong> {questionTypeOptions.find(opt => opt.value === q.questionType)?.label}</p>
                    <p><strong>Language:</strong> {languageOptions.find(opt => opt.value === q.language)?.label}</p>
                    <p><strong>Marks:</strong> {q.marks}</p>
                    <div className="code-preview">
                      <pre>{q.codeSnippet}</pre>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default AdminCodeSnippetUpload; 